import { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  RequiredFieldsText,
  RequiredItem,
} from "@transfr-inc/dashboard-components";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import {
  SearchBox,
  Select,
  useSearch,
} from "@transfr-inc/dashboard-components/forms";

import { LessonOptionItem } from "@views/classrooms/creation/lesson-option-item";
import { UserOptionItem } from "@views/classrooms/creation/user-option-item";
import { UserSelect } from "@views/classrooms/creation/user-select";

export function CourseGradebookFiltersInfo({
  className,
  gradebookData,
  onFiltersChangeHandler,
  defaultUnit,
  selectedUser,
}) {
  const { control, setValue } = useFormContext();

  const [units, setUnits] = useState([]);

  const selectedUnit = useWatch({ name: "unit" });
  const selectedLessons = useWatch({ name: "lessons" });
  const selectedTrainees = useWatch({ name: "trainees" });

  const onUnitsNameTermChange = (term) => {
    const results = unitNamesSearch.queryData(term);
    setUnits(results);
  };

  const onUnitResultSelected = (option) => {
    setUnits([option]);
    setValue("unit", option);
    setValue("lessons", []);
    setValue("trainees", []);
    onFiltersChangeHandler({ unit: option, lessons: [], trainees: [] });
  };

  const unitNamesSearch = useSearch({
    data: units,
    searchKeys: ["title"],
  });

  useEffect(() => {
    if (gradebookData) {
      const data = [...gradebookData.values()];
      setUnits(data);
      setValue("unit", defaultUnit);
      if (selectedUser) {
        setValue("trainees", [new UserOptionItem(selectedUser)]);
      }
      unitNamesSearch.updateData(data);
    }
  }, [gradebookData, defaultUnit, selectedUser]);

  useEffect(() => {
    onFiltersChangeHandler({
      unit: selectedUnit ?? {},
      lessons: selectedLessons ?? [],
      trainees: selectedTrainees ?? [],
    });
  }, [selectedUnit, selectedLessons, selectedTrainees]);

  const isVirtualUnit =
    selectedUnit?.isVirtualUnit ?? defaultUnit?.isVirtualUnit ?? false;
  const unitLabel = isVirtualUnit ? "Lesson" : "Unit";
  const unitPlaceholder = isVirtualUnit
    ? "Search or select a lesson"
    : "Search or select a unit";
  const unitOptionLabel = (r) => {
    const label = r.isVirtualUnit ? "Lesson" : "Unit";
    return `${label} ${r.orderNumber}: ${r.title}`;
  };

  return (
    <div className={className}>
      <div className="text-with-info">
        <RequiredFieldsText />
      </div>
      <div className="unit">
        <div className="section-title">
          <RequiredItem text={unitLabel} />
        </div>

        <Controller
          control={control}
          name="unit"
          rules={{ required: true }}
          render={() => {
            return (
              <SearchBox
                placeholder={unitPlaceholder}
                onSearchTermChange={onUnitsNameTermChange}
                onResultSelected={onUnitResultSelected}
                customOptionLabel={unitOptionLabel}
                query={unitNamesSearch.queryData}
                clearInputEnabled={false}
                showAllDataEnabled
                defaultOptionSelected={defaultUnit}
              ></SearchBox>
            );
          }}
        />
      </div>
      {!isVirtualUnit && (
        <div className="course-section">
          <div className="lessons">
            <div className="section-title">
              {"Lessons"}
              {selectedUnit && `: ${selectedUnit?.title}`}
            </div>
            <Controller
              control={control}
              name="lessons"
              rules={{ required: false }}
              render={({ field: { onChange, value } }) => {
                return (
                  <Select
                    id="lesson-select"
                    icon={["fa-light", "fa-magnifying-glass"]}
                    placeholder={
                      selectedLessons?.length > 0 ? "" : "All Lessons"
                    }
                    onOptionsChange={onChange}
                    options={
                      selectedUnit &&
                      selectedUnit?.lessons?.map(
                        (lesson) => new LessonOptionItem(lesson)
                      )
                    }
                    selected={value}
                    searchEnabled
                  ></Select>
                );
              }}
            />
          </div>
        </div>
      )}
      <div className="info-section">
        <div className="trainees">
          <div className="section-title">{`${getLearnerTerm({
            plural: true,
          })}`}</div>
          <Controller
            control={control}
            name="trainees"
            rules={{ required: false }}
            render={({ field: { onChange, value } }) => {
              return (
                <UserSelect
                  placeholder={
                    selectedTrainees?.length > 0
                      ? ""
                      : `All ${getLearnerTerm({ plural: true })}`
                  }
                  onChange={onChange}
                  data={(selectedUnit && selectedUnit?.users) || []}
                  selected={value}
                />
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
